/**
 * Linear usage meter — seats, storage, quota. Colour steps with utilisation.
 * Spec: assets/handoffs/design_system_export/specs/ProgressMeter.json
 *
 * @param {number} value
 * @param {number=100} max
 * @param {string=} label - primary text, e.g. "Seats used"
 * @param {string=} secondaryText - e.g. "4 of 5"
 * @param {string=} note - never the only warning; threshold colour is redundant with it
 * @param {'warning'|'error'='warning'} noteVariant
 * @param {{label:string,onClick:function}=} noteAction
 */
import React from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./ProgressMeter.module.scss";

const ProgressMeter = ({
  value,
  max = 100,
  label,
  secondaryText,
  note,
  noteVariant = "warning",
  noteAction,
  className,
  ...rest
}) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, Math.round((value / max) * 100))) : 0;
  const level = pct >= 100 ? "error" : pct >= 80 ? "warning" : "brand";

  return (
    <div className={classNames(styles.root, className)} {...rest}>
      {(label || secondaryText) && (
        <div className={styles.labelRow}>
          {label && <span className={styles.primary}>{label}</span>}
          {secondaryText && <span className={styles.secondary}>{secondaryText}</span>}
        </div>
      )}
      <div
        className={styles.track}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
      >
        <div
          className={classNames(styles.fill, styles[`fill--${level}`])}
          style={{ width: `${pct}%` }}
        />
      </div>
      {note && (
        <div className={classNames(styles.note, styles[`note--${noteVariant}`])}>
          <Icon name="AlertCircle" size={16} />
          <span>{note}</span>
          {noteAction && (
            <button type="button" className={styles.noteLink} onClick={noteAction.onClick}>
              {noteAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressMeter;
